import { Toggle } from '@/components/inputs';
import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { CustomConnectorDto } from '@/models/connectors';
import { useDeleteConnectorInstanceMutation } from '@/services/endpoints/workspace-connectors';
import { ChangeEvent, FC, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomConnectorAuthModal } from './CustomConnectorAuthModal';
import { CustomConnectorDeauthModal } from './CustomConnectorDeauthModal';

type CustomConnectorConnectionToggleProps = {
  connector: CustomConnectorDto;
  deauthenticateDisabled?: boolean;
  onChangeConnectionInProgress?: (connector: CustomConnectorDto | null) => void;
};

export const CustomConnectorConnectionToggle: FC<CustomConnectorConnectionToggleProps> = ({
  connector,
  deauthenticateDisabled = true,
  onChangeConnectionInProgress,
}) => {
  const { t } = useTranslation();
  const { id: workspaceId } = useWorkspace();
  const [deleteConnectorInstance, { isLoading: isDeleting }] = useDeleteConnectorInstanceMutation();

  const isConfigured = connector.isAuthenticated;
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  const [isAuthModalShown, toggleAuthModal] = useReducer((state) => !state, false);
  const [isDeauthModalShown, toggleDeauthModal] = useReducer((state) => !state, false);

  const handleConnectorConnection = ($event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = $event.target;
    if (checked && !isConfigured) {
      toggleAuthModal();
    } else if (!checked && isConfigured) {
      toggleDeauthModal();
    }
  };

  const connectorConnectionInProgress = (status: boolean) => {
    setConnectionInProgress(status);
    onChangeConnectionInProgress?.(status ? connector : null);
  };

  const handleDeauthSuccess = () => {
    toggleDeauthModal();
    onChangeConnectionInProgress?.(connector);
  };

  return (
    <Loadable className='h-5' isLoading={connectionInProgress || isDeleting}>
      <Toggle
        label={t(isConfigured ? 'ALL:destinationConnected' : 'ALL:destinationDisconnected')}
        labelPosition='end'
        labelWrapperClassName='capitalize text-sm text-black-100'
        checked={isConfigured}
        onChange={(e) => handleConnectorConnection(e)}
        disabled={deauthenticateDisabled}
      />
      {isAuthModalShown && (
        <CustomConnectorAuthModal
          connector={connector}
          onClose={toggleAuthModal}
          connectorConnectionInProgress={connectorConnectionInProgress}
          onChangeConnectionInProgress={onChangeConnectionInProgress}
        />
      )}
      {isDeauthModalShown && (
        <CustomConnectorDeauthModal workspaceId={workspaceId} connector={connector} onSuccess={handleDeauthSuccess} onClose={toggleDeauthModal} />
      )}
    </Loadable>
  );
};
